import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/lang";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import Reveal from "@/components/ui-brand/Reveal";
import SGBReturnPill from "@/components/sgb/SGBReturnPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Minimal SGB-styled header & footer (independent from SGBPage to avoid the
// big single-page-app surface). Kept slim & purposeful — legal pages should
// feel quiet and legible.
// ---------------------------------------------------------------------------
const SGBLegalHeader = () => (
  <header className="sticky top-0 z-40 bg-depth/95 backdrop-blur-xl border-b border-warmth/20">
    <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between gap-6 text-canvas">
      <Link
        to="/sgb"
        data-testid="sgb-legal-brand"
        className="flex items-baseline gap-3 select-none"
      >
        <span className="font-display text-xl md:text-[1.45rem] leading-none">
          Sichtbar <span className="italic-serif text-signal">·</span> Gut Bewertet
        </span>
      </Link>
      <Link
        to="/sgb"
        data-testid="sgb-legal-back"
        className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth/85 hover:text-canvas transition-colors"
      >
        ← zurück zur Startseite
      </Link>
    </div>
  </header>
);

const SGBLegalFooter = () => (
  <footer className="bg-depth text-canvas mt-24 border-t border-warmth/20">
    <div className="px-6 md:px-12 lg:px-16 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
      <div>
        <p className="font-mono text-[0.55rem] tracking-[0.32em] uppercase text-warmth/70 mb-3">
          Anbieter
        </p>
        <p>Patrice Schreiter · Einzelunternehmerin</p>
        <p>Ingelheimer Str. 9 · 09212 Limbach-Oberfrohna</p>
      </div>
      <div>
        <p className="font-mono text-[0.55rem] tracking-[0.32em] uppercase text-warmth/70 mb-3">
          Kontakt
        </p>
        <p>kontakt@sichtbargutbewertet.de</p>
        <p>+49 (0) 3722 7981957</p>
      </div>
      <div>
        <p className="font-mono text-[0.55rem] tracking-[0.32em] uppercase text-warmth/70 mb-3">
          Rechtliches
        </p>
        <ul className="space-y-1">
          <li><Link to="/sgb/impressum" className="hover:text-signal transition-colors">Impressum</Link></li>
          <li><Link to="/sgb/datenschutz" className="hover:text-signal transition-colors">Datenschutz</Link></li>
          <li><Link to="/sgb/agb" className="hover:text-signal transition-colors">AGB</Link></li>
          <li><Link to="/sgb/widerruf" className="hover:text-signal transition-colors">Widerrufsbelehrung</Link></li>
        </ul>
      </div>
    </div>
    <div className="px-6 md:px-12 lg:px-16 py-4 border-t border-warmth/15 text-[0.62rem] font-mono tracking-[0.22em] uppercase text-warmth/65">
      © {new Date().getFullYear()} Sichtbar Gut Bewertet · Patrice Schreiter
    </div>
  </footer>
);

// ---------------------------------------------------------------------------
// Editorial primitives
// ---------------------------------------------------------------------------
const Section = ({ eyebrow, title, children }) => (
  <section className="border-t border-warmth/25 pt-10 mt-12 first:border-t-0 first:mt-0 first:pt-0">
    {eyebrow && (
      <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere mb-3">
        {eyebrow}
      </p>
    )}
    {title && (
      <h2 className="font-display text-2xl md:text-3xl leading-tight text-depth mb-6">
        {title}
      </h2>
    )}
    <div className="space-y-5 text-depth/90 font-light text-base md:text-[1.0625rem] leading-relaxed">
      {children}
    </div>
  </section>
);

const Sub = ({ children }) => (
  <h3 className="font-display italic text-xl md:text-2xl text-depth mt-6 mb-3">
    {children}
  </h3>
);

// ---------------------------------------------------------------------------
// Content blocks
// ---------------------------------------------------------------------------
const Impressum = () => (
  <>
    <Section eyebrow="01 — Anbieter" title="Impressum">
      <p>
        <strong>Patrice Schreiter</strong>
        <br />
        Einzelunternehmerin
        <br />
        Ingelheimer Straße 9
        <br />
        09212 Limbach-Oberfrohna
      </p>
      <Sub>Kontakt</Sub>
      <p>
        Telefon: +49 (0) 3722 7981957
        <br />
        E-Mail: <a className="text-signal hover:underline" href="mailto:kontakt@sichtbargutbewertet.de">kontakt@sichtbargutbewertet.de</a>
      </p>
      <Sub>Umsatzsteuer-ID</Sub>
      <p>
        Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
        <br />
        <strong>DE455365728</strong>
      </p>
      <Sub>Verbraucherstreitbeilegung / Universalschlichtungsstelle</Sub>
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
      <p className="text-atmosphere/70 text-sm pt-6">Quelle: eRecht24</p>
    </Section>
  </>
);

const Datenschutz = () => (
  <>
    <Section eyebrow="01 — Auf einen Blick" title="Datenschutzerklärung">
      <Sub>Allgemeine Hinweise</Sub>
      <p>
        Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
        personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene
        Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
        Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem
        Text aufgeführten Datenschutzerklärung.
      </p>
      <Sub>Datenerfassung auf dieser Website</Sub>
      <p>
        <em>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</em>
        <br />
        Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen
        Kontaktdaten können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in
        dieser Datenschutzerklärung entnehmen.
      </p>
      <p>
        <em>Wie erfassen wir Ihre Daten?</em>
        <br />
        Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei
        kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
        Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der
        Website durch unsere IT-Systeme erfasst (technische Daten wie Internetbrowser,
        Betriebssystem, Uhrzeit des Seitenaufrufs).
      </p>
      <p>
        <em>Wofür nutzen wir Ihre Daten?</em>
        <br />
        Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website
        zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet
        werden. Sofern über die Website Verträge geschlossen oder angebahnt werden,
        werden die übermittelten Daten auch für Vertragsangebote, Bestellungen oder
        sonstige Auftragsanfragen verarbeitet.
      </p>
      <p>
        <em>Welche Rechte haben Sie bezüglich Ihrer Daten?</em>
        <br />
        Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger
        und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben
        außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen.
        Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese
        jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter
        bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen
        Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen
        Aufsichtsbehörde zu.
      </p>
    </Section>

    <Section eyebrow="02 — Hosting" title="Wo läuft diese Website?">
      <p>Wir hosten die Inhalte unserer Website bei folgendem Anbieter:</p>
      <Sub>Emergent</Sub>
      <p>
        Anbieter der Hosting-Infrastruktur ist Emergent. Die Verwendung erfolgt auf
        Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse
        an einer zuverlässigen Darstellung unserer Website. Sofern eine entsprechende
        Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf
        Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TDDDG, soweit die
        Einwilligung die Speicherung von Cookies oder den Zugriff auf Informationen im
        Endgerät des Nutzers im Sinne des TDDDG umfasst. Die Einwilligung ist jederzeit
        widerrufbar.
      </p>
      <Sub>Auftragsverarbeitung</Sub>
      <p>
        Wir haben einen Vertrag über Auftragsverarbeitung (AVV) zur Nutzung des oben
        genannten Dienstes geschlossen. Hierbei handelt es sich um einen
        datenschutzrechtlich vorgeschriebenen Vertrag, der gewährleistet, dass dieser
        die personenbezogenen Daten unserer Websitebesucher nur nach unseren Weisungen
        und unter Einhaltung der DSGVO verarbeitet.
      </p>
    </Section>

    <Section eyebrow="03 — Pflichtinformationen" title="Allgemeine Hinweise">
      <Sub>Datenschutz</Sub>
      <p>
        Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr
        ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend
        den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung. Wenn
        Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben.
        Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der
        Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser
        Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.
      </p>
      <Sub>Hinweis zur verantwortlichen Stelle</Sub>
      <p>
        Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
        <br />
        Patrice Schreiter · Einzelunternehmerin
        <br />
        Ingelheimer Straße 9 · 09212 Limbach-Oberfrohna
        <br />
        Telefon: +49 (0) 3722 7981957
        <br />
        E-Mail: <a className="text-signal hover:underline" href="mailto:kontakt@sichtbargutbewertet.de">kontakt@sichtbargutbewertet.de</a>
      </p>
      <Sub>Speicherdauer</Sub>
      <p>
        Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer
        genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck
        für die Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschersuchen
        geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, werden
        Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für
        die Speicherung Ihrer personenbezogenen Daten haben.
      </p>
      <Sub>Rechtsgrundlagen der Datenverarbeitung</Sub>
      <p>
        Sofern Sie in die Datenverarbeitung eingewilligt haben, verarbeiten wir Ihre
        personenbezogenen Daten auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO bzw.
        Art. 9 Abs. 2 lit. a DSGVO. Sind Ihre Daten zur Vertragserfüllung oder zur
        Durchführung vorvertraglicher Maßnahmen erforderlich, verarbeiten wir Ihre
        Daten auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO. Des Weiteren verarbeiten
        wir Ihre Daten, sofern diese zur Erfüllung einer rechtlichen Verpflichtung
        erforderlich sind auf Grundlage von Art. 6 Abs. 1 lit. c DSGVO. Die
        Datenverarbeitung kann ferner auf Grundlage unseres berechtigten Interesses
        nach Art. 6 Abs. 1 lit. f DSGVO erfolgen.
      </p>
      <Sub>Widerruf Ihrer Einwilligung</Sub>
      <p>
        Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung
        möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen.
        Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom
        Widerruf unberührt.
      </p>
      <Sub>Widerspruchsrecht (Art. 21 DSGVO)</Sub>
      <p className="uppercase tracking-wide text-sm">
        Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e oder f DSGVO
        erfolgt, haben Sie jederzeit das Recht, aus Gründen, die sich aus Ihrer
        besonderen Situation ergeben, gegen die Verarbeitung Ihrer personenbezogenen
        Daten Widerspruch einzulegen; dies gilt auch für ein auf diese Bestimmungen
        gestütztes Profiling. Werden Ihre personenbezogenen Daten verarbeitet, um
        Direktwerbung zu betreiben, so haben Sie das Recht, jederzeit Widerspruch
        gegen die Verarbeitung Sie betreffender personenbezogener Daten zum Zwecke
        derartiger Werbung einzulegen.
      </p>
      <Sub>Beschwerderecht bei der Aufsichtsbehörde</Sub>
      <p>
        Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht
        bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen
        Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu.
      </p>
      <Sub>Recht auf Datenübertragbarkeit</Sub>
      <p>
        Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in
        Erfüllung eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten
        in einem gängigen, maschinenlesbaren Format aushändigen zu lassen.
      </p>
      <Sub>Auskunft, Berichtigung und Löschung</Sub>
      <p>
        Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht
        auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten,
        deren Herkunft und Empfänger und den Zweck der Datenverarbeitung sowie ggf.
        ein Recht auf Berichtigung oder Löschung dieser Daten.
      </p>
    </Section>

    <Section eyebrow="04 — Datenerfassung" title="Was auf dieser Website konkret passiert">
      <Sub>Kontaktformular</Sub>
      <p>
        Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben
        aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten
        zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns
        gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
      </p>
      <p>
        Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b
        DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder
        zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen
        Fällen beruht die Verarbeitung auf unserem berechtigten Interesse (Art. 6 Abs. 1
        lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO); die
        Einwilligung ist jederzeit widerrufbar.
      </p>
      <p>
        Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie
        uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder
        der Zweck für die Datenspeicherung entfällt.
      </p>
      <Sub>Anfrage per E-Mail oder WhatsApp</Sub>
      <p>
        Wenn Sie mich per E-Mail oder über die auf dieser Website angegebenen
        Kontaktmöglichkeiten (z. B. WhatsApp) kontaktieren, werden die von Ihnen
        übermittelten personenbezogenen Daten ausschließlich zur Bearbeitung Ihrer
        Anfrage gespeichert und verarbeitet.
      </p>
      <p>
        Die Kontaktaufnahme per E-Mail erfolgt über einen externen E-Mail-Dienstleister
        (IONOS). Dabei können die übermittelten Daten auf den Servern des Anbieters
        verarbeitet werden.
      </p>
      <p>
        Bei einer Kontaktaufnahme über WhatsApp erfolgt die Kommunikation über den
        Messaging-Dienst WhatsApp. Anbieter ist die WhatsApp Ireland Limited, 4 Grand
        Canal Square, Grand Canal Harbour, Dublin 2, Irland. WhatsApp ist ein Dienst der
        Meta Platforms Inc. Dabei können personenbezogene Daten auch auf Servern in
        Staaten außerhalb der Europäischen Union übertragen werden.
      </p>
      <p>
        Die Verarbeitung der Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO
        (Bearbeitung von Anfragen) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
        Interesse an einer effizienten Kommunikation).
      </p>
      <p className="text-atmosphere/70 text-sm pt-6">Quelle: e-recht24.de</p>
    </Section>
  </>
);

const AGB = () => (
  <>
    <Section eyebrow="Allgemeine Geschäftsbedingungen" title="Für die Buchung von Dienstleistungen auf sichtbargutbewertet.de">
      <p>
        Patrice Schreiter
        <br />
        Ingelheimer Straße 9 · 09212 Limbach-Oberfrohna
        <br />
        E-Mail: kontakt@sichtbargutbewertet.de · Telefon: +49 (0) 3722 7981957
        <br />
        — im Folgenden: <em>Anbieterin</em>.
      </p>
    </Section>

    <Section eyebrow="§ 1" title="Geltungsbereich">
      <p>
        Diese Allgemeinen Geschäftsbedingungen (AGB) gelten nach ihrer Einbeziehung für
        alle Vertragsschlüsse über die Buchung von Dienstleistungen (z. B. Beratung,
        Profilanalysen, Bewertungsmanagement, Webdesign, individuelle Pakete) über die
        Webseite unter <strong>www.sichtbargutbewertet.de</strong> in ihrer zum Zeitpunkt
        des Vertragsschlusses gültigen Fassung.
      </p>
      <p>
        Diese AGB gelten ausschließlich. Abweichende AGB des Kunden werden nicht
        Vertragsbestandteil, es sei denn, die Anbieterin stimmt diesen ausdrücklich zu.
      </p>
    </Section>

    <Section eyebrow="§ 2" title="Vertragsschluss">
      <p>
        2.1 Die Darstellung der Dienstleistungen auf der Webseite stellt kein verbindliches
        Angebot dar, sondern eine Aufforderung an die Besucher, ein Angebot zur Beauftragung
        der dargestellten Dienstleistungen abzugeben.
      </p>
      <p>
        2.2 Die Anfrage einer Dienstleistung erfolgt über die auf der Webseite
        bereitgestellten Formulare (Kontakt- bzw. Buchungsformular) oder per E-Mail. Mit
        Absenden der Anfrage gibt der Kunde ein verbindliches Angebot zum Abschluss eines
        Dienstleistungsvertrags ab. Der Vertragsschluss erfolgt durch Annahme der Anbieterin
        in Schrift- oder Textform (z. B. per E-Mail).
      </p>
      <p>
        2.3 Vor der verbindlichen Abgabe der Anfrage kann der Kunde seine Eingaben jederzeit
        über die üblichen Eingabefunktionen prüfen und korrigieren.
      </p>
      <p>
        2.4 Die Anbieterin speichert den Vertragstext nach Vertragsschluss und übermittelt
        ihn dem Kunden in Textform (z. B. per E-Mail). Eine darüber hinausgehende
        Zugänglichmachung erfolgt nicht.
      </p>
      <p>2.5 Für den Vertragsschluss steht folgende Sprache zur Verfügung: Deutsch.</p>
    </Section>

    <Section eyebrow="§ 3" title="Widerrufsrecht für Verbraucher">
      <p>
        Verbrauchern steht bei außerhalb von Geschäftsräumen geschlossenen Verträgen und bei
        Fernabsatzverträgen grundsätzlich ein Widerrufsrecht zu. Verbraucher ist jede
        natürliche Person, die ein Rechtsgeschäft zu Zwecken abschließt, die überwiegend
        weder ihrer gewerblichen noch ihrer selbstständigen beruflichen Tätigkeit zugerechnet
        werden können.
      </p>
      <p>
        Details sind der <Link to="/sgb/widerruf" className="text-signal hover:underline">Widerrufsbelehrung</Link> zu
        entnehmen, die jedem Verbraucher spätestens unmittelbar vor Vertragsschluss zur
        Verfügung gestellt wird.
      </p>
      <Sub>Hinweis zum Kundenkreis</Sub>
      <p>
        Dieses Angebot richtet sich ausschließlich an Unternehmer im Sinne des § 14 BGB.
        Verbraucher im Sinne des § 13 BGB sind von der Nutzung der angebotenen
        Dienstleistungen ausgeschlossen. Sollte in Einzelfällen dennoch ein Vertrag mit
        einem Verbraucher zustande kommen, beginnt die Ausführung der Dienstleistung
        unmittelbar nach Bestellung. Mit Beginn der Ausführung der Dienstleistung kann ein
        eventuell bestehendes Widerrufsrecht gemäß § 356 Abs. 4 BGB vorzeitig erlöschen.
      </p>
    </Section>

    <Section eyebrow="§ 4" title="Vergütung & Zahlungsbedingungen">
      <p>
        4.1 Es gelten die zum Zeitpunkt des Vertragsschlusses individuell vereinbarten
        Vergütungen. Alle Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer,
        sofern nicht ausdrücklich anders ausgewiesen.
      </p>
      <p>
        4.2 Die Zahlung erfolgt — sofern nicht anders vereinbart — per Banküberweisung
        innerhalb der auf der Rechnung genannten Zahlungsfrist. Weitere Zahlungsmittel
        (z. B. PayPal, SEPA-Lastschrift, Kreditkarte) können nach gesonderter Vereinbarung
        zur Verfügung gestellt werden.
      </p>
      <p>
        4.3 Sofern die Anbieterin und der Kunde Abschlagszahlungen oder eine Vorauszahlung
        vereinbart haben, wird die Dienstleistung erst nach Eingang der entsprechenden
        Zahlung erbracht.
      </p>
    </Section>

    <Section eyebrow="§ 5" title="Leistungserbringung & Mitwirkung">
      <p>
        5.1 Die Anbieterin erbringt die vereinbarten Dienstleistungen mit der erforderlichen
        Sorgfalt und Fachkenntnis. Konkrete Erfolge oder bestimmte messbare Ergebnisse
        werden — sofern nicht ausdrücklich schriftlich zugesichert — nicht geschuldet.
      </p>
      <p>
        5.2 Der Kunde ist verpflichtet, alle für die ordnungsgemäße Leistungserbringung
        notwendigen Informationen, Zugänge und Materialien rechtzeitig und in geeigneter
        Form zur Verfügung zu stellen. Verzögerungen, die durch fehlende Mitwirkung des
        Kunden entstehen, gehen nicht zu Lasten der Anbieterin.
      </p>
    </Section>

    <Section eyebrow="§ 6" title="Haftung & Freistellung">
      <p>
        6.1 Die Anbieterin haftet unbeschränkt für Schäden aus der Verletzung des Lebens,
        des Körpers oder der Gesundheit, die auf einer vorsätzlichen oder fahrlässigen
        Pflichtverletzung der Anbieterin oder einer ihrer Erfüllungsgehilfen beruhen, sowie
        für Schäden, die auf einer vorsätzlichen oder grob fahrlässigen Pflichtverletzung
        beruhen, aufgrund eines Garantieversprechens und aufgrund zwingender Haftung
        (z. B. nach dem Produkthaftungsgesetz).
      </p>
      <p>
        6.2 Bei leichter Fahrlässigkeit haftet die Anbieterin nur bei der Verletzung
        wesentlicher Vertragspflichten. In diesem Fall ist die Haftung auf den
        vertragstypischen, vorhersehbaren Schaden begrenzt. Wesentliche Vertragspflichten
        sind Pflichten, deren Erfüllung die ordnungsgemäße Durchführung des Vertrags
        überhaupt erst ermöglicht und auf deren Einhaltung der Kunde regelmäßig vertrauen
        darf.
      </p>
      <p>
        6.3 Im Übrigen ist eine Haftung der Anbieterin sowie die Haftung ihrer
        Erfüllungsgehilfen und gesetzlichen Vertreter ausgeschlossen.
      </p>
      <p>
        6.4 Der Kunde stellt die Anbieterin von jeglichen Ansprüchen Dritter — einschließlich
        der Kosten für die Rechtsverteidigung in ihrer gesetzlichen Höhe — frei, die gegen
        die Anbieterin aufgrund von rechts- oder vertragswidrigen Handlungen des Kunden
        geltend gemacht werden.
      </p>
    </Section>

    <Section eyebrow="§ 7" title="Datenschutz">
      <p>
        Die Anbieterin behandelt die personenbezogenen Daten ihrer Kunden vertraulich und
        im Einklang mit den gesetzlichen Datenschutzvorschriften. Näheres entnehmen Sie der{" "}
        <Link to="/sgb/datenschutz" className="text-signal hover:underline">
          Datenschutzerklärung
        </Link>
        .
      </p>
    </Section>

    <Section eyebrow="§ 8" title="Schlussbestimmungen">
      <p>
        8.1 Anwendbar ist das Recht der Bundesrepublik Deutschland unter Ausschluss des
        UN-Kaufrechts, soweit diese Rechtswahl nicht dazu führt, dass ein Verbraucher mit
        gewöhnlichem Aufenthalt in der EU hierdurch zwingenden gesetzlichen Bestimmungen
        des Rechts seines Aufenthaltsstaates entzogen wird.
      </p>
      <p>
        8.2 Ist der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder
        öffentlich-rechtliches Sondervermögen, ist das Gericht am Sitz der Anbieterin
        zuständig, sofern nicht für die Streitigkeit ein ausschließlicher Gerichtsstand
        begründet ist.
      </p>
      <p>
        8.3 Soweit eine Bestimmung dieses Vertrages ungültig oder undurchsetzbar ist oder
        wird, bleiben die übrigen Bestimmungen hiervon unberührt.
      </p>
    </Section>

    <Section eyebrow="§ 9" title="Online-Streitbeilegung / Verbraucherschlichtung">
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </Section>
  </>
);

// ---------------------------------------------------------------------------
// Interactive Widerruf form
// ---------------------------------------------------------------------------
const WiderrufForm = () => {
  const [form, setForm] = useState({
    name: "",
    address: "",
    service: "",
    orderedAt: "",
    receivedAt: "",
    date: new Date().toLocaleDateString("de-DE"),
    signature: "",
  });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.service) {
      setStatus("error");
      setMsg("Bitte fülle mindestens Name und Bezeichnung der Dienstleistung aus.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/widerruf`, { ...form, source: "sichtbargutbewertet" });
      setStatus("success");
      setMsg(
        "Dein Widerruf ist eingegangen. Du bekommst gleich eine Bestätigung per E-Mail. Eine Kopie liegt bei Patrice."
      );
    } catch (err) {
      setStatus("error");
      setMsg("Etwas ist schiefgelaufen. Bitte versuche es erneut oder schicke eine formlose E-Mail an kontakt@sichtbargutbewertet.de.");
    }
  };

  const labelClass = "block font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere mb-2";
  const inputClass =
    "w-full bg-transparent border-b border-depth/30 focus:border-depth focus:outline-none py-2 text-depth font-light text-base placeholder:text-warmth/60 transition-colors";
  const textareaClass = `${inputClass} resize-none min-h-[88px]`;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="sgb-widerruf-form"
      className="mt-12 space-y-8 max-w-3xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        <div>
          <label className={labelClass} htmlFor="wf-name">Name des Verbrauchers / der Verbraucherin</label>
          <input
            id="wf-name"
            data-testid="wf-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            className={inputClass}
            placeholder="Vor- & Nachname"
            required
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="wf-address">Anschrift</label>
          <textarea
            id="wf-address"
            data-testid="wf-address"
            value={form.address}
            onChange={update("address")}
            className={textareaClass}
            placeholder="Straße · PLZ · Ort"
            rows={2}
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="wf-service">Bezeichnung der Dienstleistung (ggf. Bestellnummer & Preis)</label>
        <textarea
          id="wf-service"
          data-testid="wf-service"
          value={form.service}
          onChange={update("service")}
          className={textareaClass}
          placeholder="z. B. CLEAR SIGNAL™ Profilanalyse, Auftragsnr. 2026-…"
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8">
        <div>
          <label className={labelClass} htmlFor="wf-ordered">Bestellt am</label>
          <input
            id="wf-ordered"
            data-testid="wf-ordered"
            type="text"
            value={form.orderedAt}
            onChange={update("orderedAt")}
            className={inputClass}
            placeholder="TT.MM.JJJJ"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="wf-received">Erhalten am</label>
          <input
            id="wf-received"
            data-testid="wf-received"
            type="text"
            value={form.receivedAt}
            onChange={update("receivedAt")}
            className={inputClass}
            placeholder="TT.MM.JJJJ"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="wf-date">Datum heute</label>
          <input
            id="wf-date"
            data-testid="wf-date"
            type="text"
            value={form.date}
            onChange={update("date")}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="wf-signature">Unterschrift (Name in Druckbuchstaben, nur bei schriftlichem Widerruf)</label>
        <input
          id="wf-signature"
          data-testid="wf-signature"
          type="text"
          value={form.signature}
          onChange={update("signature")}
          className={inputClass}
          placeholder="Dein Name"
        />
      </div>

      <div className="pt-4 flex flex-col md:flex-row md:items-center gap-6">
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="wf-submit"
          className="px-8 py-4 bg-depth text-canvas font-mono text-[0.7rem] tracking-[0.32em] uppercase hover:bg-signal transition-colors disabled:opacity-50"
        >
          {status === "sending" ? "Wird gesendet …" : "Widerruf absenden ↗"}
        </button>
        <button
          type="button"
          data-testid="wf-print"
          onClick={() => window.print()}
          className="font-mono text-[0.65rem] tracking-[0.32em] uppercase text-depth hover:text-signal transition-colors"
        >
          Formular drucken
        </button>
      </div>

      {msg && status !== "sending" && (
        <p
          data-testid="wf-status"
          className={`mt-4 text-sm font-light ${status === "error" ? "text-signal" : "text-atmosphere"}`}
        >
          {msg}
        </p>
      )}
    </form>
  );
};

const Widerruf = () => (
  <>
    <Section eyebrow="01 — Widerrufsausschluss" title="Vorzeitiger Beginn der Dienstleistung">
      <p>
        Wird die Dienstleistung auf ausdrücklichen Wunsch des Kunden vor Ablauf der
        14-tägigen Widerrufsfrist begonnen, erlischt das Widerrufsrecht mit
        vollständiger Erfüllung des Vertrags. Vor Vertragsschluss bestätigt der
        Kunde dies über folgende, nicht vorangeklickte Erklärung:
      </p>

      <label
        htmlFor="widerruf-consent-demo"
        className="mt-4 flex items-start gap-3 p-5 border border-atmosphere/25 bg-atmosphere/[0.04] cursor-pointer hover:border-signal/50 transition-colors"
      >
        <input
          id="widerruf-consent-demo"
          type="checkbox"
          data-testid="widerruf-consent-demo"
          className="mt-1 h-5 w-5 accent-[#ff7100] cursor-pointer flex-shrink-0"
        />
        <span className="text-depth font-light leading-relaxed">
          Ich verlange und stimme ausdrücklich zu, dass Sie vor Ende der Widerrufsfrist
          mit der in Auftrag gegebenen Dienstleistung beginnen. Mir ist bekannt, dass
          mein Widerrufsrecht bei vollständiger Erfüllung des Vertrags erlischt.
        </span>
      </label>

      <p className="text-atmosphere font-light italic mt-4 text-sm">
        Im Bestellprozess erscheint diese Erklärung als verpflichtende, nicht
        vorangeklickte Checkbox unmittelbar vor dem Klick auf "Jetzt zahlungspflichtig
        bestellen". Die hier gezeigte Box dient der vorvertraglichen Information.
        Unabhängig vom Widerrufsausschluss erhält der Kunde zusätzlich diese
        Widerrufsbelehrung und das Muster-Widerrufsformular.
      </p>
    </Section>

    <Section eyebrow="02 — Widerrufsbelehrung" title="Widerrufsrecht für Verbraucher">
      <p>
        Verbraucher ist jede natürliche Person, die ein Rechtsgeschäft zu Zwecken abschließt,
        die überwiegend weder ihrer gewerblichen noch ihrer selbständigen beruflichen Tätigkeit
        zugerechnet werden können.
      </p>
      <p>
        Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu
        widerrufen. Die Widerrufsfrist beträgt 14 Tage ab dem Tag des Vertragsabschlusses.
      </p>
      <p>
        Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (Sichtbar Gut Bewertet, Patrice
        Schreiter, Ingelheimer Str. 9, 09212 Limbach-Oberfrohna, Telefon:
        +49 (0) 3722 7981957, E-Mail:{" "}
        <a className="text-signal hover:underline" href="mailto:kontakt@sichtbargutbewertet.de">
          kontakt@sichtbargutbewertet.de
        </a>
        ) mittels einer eindeutigen Erklärung (z. B. ein mit der Post versandter Brief oder
        eine E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie
        können dafür das beigefügte Muster-Widerrufsformular verwenden, das jedoch
        nicht vorgeschrieben ist.
      </p>
      <p>
        Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die
        Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
      </p>
      <Sub>Folgen des Widerrufs</Sub>
      <p>
        Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von
        Ihnen erhalten haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen
        Kosten, die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von
        uns angebotene, günstigste Standardlieferung gewählt haben), unverzüglich und
        spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung
        über Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für diese Rückzahlung
        verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion
        eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes
        vereinbart; in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
      </p>
      <p>
        Haben Sie verlangt, dass die Dienstleistung während der Widerrufsfrist beginnen
        soll, so haben Sie uns einen angemessenen Betrag zu zahlen, der dem Anteil der bis
        zu dem Zeitpunkt, zu dem Sie uns von der Ausübung des Widerrufsrechts hinsichtlich
        dieses Vertrags unterrichten, bereits erbrachten Dienstleistungen im Vergleich zum
        Gesamtumfang der im Vertrag vorgesehenen Dienstleistungen entspricht.
      </p>
    </Section>

    <Section eyebrow="03 — Muster-Widerrufsformular" title="Wenn Sie widerrufen möchten, füllen Sie bitte das Formular aus.">
      <p className="text-atmosphere font-light italic">
        An: Sichtbar Gut Bewertet · Patrice Schreiter · Ingelheimer Str. 9 ·
        09212 Limbach-Oberfrohna · E-Mail: kontakt@sichtbargutbewertet.de
      </p>
      <p>
        Hiermit widerrufe(n) ich / wir den von mir / uns abgeschlossenen Vertrag über die
        Erbringung der folgenden Dienstleistung.
      </p>
      <WiderrufForm />
    </Section>

    <Section eyebrow="04 — Hinweis zum Kundenkreis" title="B2B — Angebot für Unternehmer">
      <p>
        Dieses Angebot richtet sich ausschließlich an Unternehmer im Sinne des § 14 BGB.
        Verbraucher im Sinne des § 13 BGB sind von der Nutzung der angebotenen
        Dienstleistungen ausgeschlossen.
      </p>
      <p>
        Sollte in Einzelfällen dennoch ein Vertrag mit einem Verbraucher zustande kommen,
        beginnt die Ausführung der Dienstleistung unmittelbar nach Bestellung. Mit Beginn
        der Ausführung der Dienstleistung kann ein eventuell bestehendes Widerrufsrecht
        gemäß § 356 Abs. 4 BGB vorzeitig erlöschen.
      </p>
    </Section>
  </>
);


// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
const TITLES = {
  imprint: { title: "Impressum", eyebrow: "— Pflichtangaben" },
  privacy: { title: "Datenschutzerklärung", eyebrow: "— DSGVO" },
  terms: { title: "Allgemeine Geschäftsbedingungen", eyebrow: "— AGB" },
  withdrawal: { title: "Widerrufsbelehrung und Widerrufsformular", eyebrow: "— Widerruf" },
};

const SGBLegalPage = ({ kind }) => {
  const location = useLocation();
  const { lang } = useLanguage();
  const meta = TITLES[kind] || TITLES.imprint;
  useDocumentMeta({
    title: `${meta.title} · Sichtbar Gut Bewertet`,
    description: `${meta.title} der Marke Sichtbar Gut Bewertet · Patrice Schreiter`,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-canvas text-depth">
      <SGBLegalHeader />
      <SGBReturnPill />
      <main className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 max-w-4xl mx-auto">
        <Reveal>
          <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere">
            {meta.eyebrow}
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-6">
            {meta.title}
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <div className="mt-12 md:mt-16">
            {kind === "imprint" && <Impressum />}
            {kind === "privacy" && <Datenschutz />}
            {kind === "terms" && <AGB />}
            {kind === "withdrawal" && <Widerruf />}
          </div>
        </Reveal>
      </main>
      <SGBLegalFooter />
    </div>
  );
};

export default SGBLegalPage;

